/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    AuthConfiguration,
    AuthConfigurationsApi,
    Configuration,
    ConfigurationParameters,
    IptvLoginApi,
    IptvLoginRequest,
    IptvLoginResponse,
    AuthConfigurationsGetRequest,
    V1IptvLoginPostRequest,
    AuthConfigurations,
    AuthenticationType,
} from 'client-code/iptv-login';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { IIptvLoginServiceRepository } from '../interfaces/IIptvLoginServiceRepository';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Storage } from '../storage/Storage';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class IptvLoginServiceRepositoryImpl implements IIptvLoginServiceRepository {
    TAG: string = 'IptvLoginServiceRepositoryImpl';

    private static baseUrl: string;

    private authConfigurationsApi;

    private iptvLoginApi;

    constructor() {
        const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;
        const configurationParameters: ConfigurationParameters = {
            basePath: IptvLoginServiceRepositoryImpl.baseUrl,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };

        const configuration = new Configuration(configurationParameters);
        this.authConfigurationsApi = new AuthConfigurationsApi(configuration);
        this.iptvLoginApi = new IptvLoginApi(configuration);
    }

    static initialize(baseUrl: string) {
        IptvLoginServiceRepositoryImpl.baseUrl = baseUrl;
    }

    /**
     * function will return network call response and update cache
     * Will return cached response if network call fails and throw error if cache unavailable
     * @returns Array<AuthConfiguration>
     */
    private async getAuthConfigurations(): Promise<AuthConfiguration> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;
            const requestParameters: AuthConfigurationsGetRequest = {
                applicationName: DeviceModel.getAppName(),
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: DeviceModel.getAppVersionName(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getAppName(),
                acceptLanguage: DeviceModel.getTVProperties().getDateLocale(),
                msoName: DeviceModel.getTiVoSettings().getMsoName(),
                slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
            };

            const authConfigurationsResponse =
                await this.authConfigurationsApi.authConfigurationsGet(requestParameters);

            const licensePlateAuthConfig = (
                authConfigurationsResponse.data as AuthConfigurations
            ).authconfigurations?.find(
                (config) => config.authenticationType === AuthenticationType.DbsLicensePlateBinding,
            );

            Storage.getInstance().setItem(Storage.Keys.AUTH_CONFIGURATIONS, licensePlateAuthConfig);
            return licensePlateAuthConfig!;
        } catch (error) {
            Log.e(this.TAG, 'getAuthConfigurations()', error);
            const authConfig = Storage.getInstance().getItem(Storage.Keys.AUTH_CONFIGURATIONS);
            if (authConfig) {
                Log.i(this.TAG, 'getAuthConfigurations()', 'returned from cache');
                return authConfig;
            }
            throw error;
        }
    }

    private async fetchIptvLogin(samlToken: string): Promise<IptvLoginResponse> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;
            const postRequestBody: IptvLoginRequest = {
                domain: DeviceModel.getTiVoSettings().getProvisioningDomain(),
                authToken: samlToken,
                deviceConfig: {
                    manufacturer: DeviceModel.getTVProperties().getPlatformName(),
                    model: DeviceModel.getTVProperties().getDeviceModel(),
                    osVersion: DeviceModel.getTVProperties().getPlatformVersion(),
                    softwareVersion: DeviceModel.getAppVersionName(),
                    timezoneName: DeviceModel.getTVProperties().getTimezone(),
                    friendlyName: DeviceModel.getFriendlyName(),
                },
                caDeviceId: DeviceModel.getCaDeviceId(),
                slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
            };

            const iptvLoginPostRequest: V1IptvLoginPostRequest = {
                applicationName: commonHeaders.ApplicationName,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: commonHeaders.ProductName,
                iptvLoginRequest: postRequestBody,
                acceptLanguage: DeviceModel.getTVProperties().getDateLocale(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const iptvLoginResponse = await this.iptvLoginApi.v1IptvLoginPost(iptvLoginPostRequest);

            return iptvLoginResponse.data;
        } catch (error) {
            Log.e(this.TAG, 'getIptvLogin()', error);
            throw error;
        }
    }

    async fetchAndGetAuthConfiguration(): Promise<AuthConfiguration> {
        try {
            return await this.getAuthConfigurations();
        } catch (error) {
            Log.e(this.TAG, 'fetchAuthConfigurations()', error);
            throw error;
        }
    }

    async getIptvLoginBySamlToken(samlToken: string): Promise<IptvLoginResponse> {
        try {
            return await this.fetchIptvLogin(samlToken);
        } catch (error) {
            Log.e(this.TAG, 'fetchIptvLogin()', error);
            throw error;
        }
    }
}
